<template>
  <div id="project_rate_setting" class="project-rate-page">
    <CCard>
      <CCardHeader>
        <h2>
          <span>プロジェクト金額設定</span>
          <span class="card-header-actions">
            <router-link to="/admin/total/list_project">
              <button type="button" class="btn btn-outline-primary">
                プロジェクト集計へ
              </button>
            </router-link>
          </span>
        </h2>
      </CCardHeader>

      <CCardBody>
        <div class="project-rate-search">
          <div>
            <label for="project-rate-year">対象年月</label>
            <div class="project-rate-search__period">
              <select
                id="project-rate-year"
                class="custom-select"
                v-model="year"
                :disabled="loading || saving"
              >
                <option
                  v-for="option in yearOption"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <select
                id="project-rate-month"
                class="custom-select"
                v-model="month"
                :disabled="loading || saving"
              >
                <option
                  v-for="option in monthOption"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <span>業務分</span>
            </div>
          </div>

          <div class="project-rate-search__actions">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || saving"
              @click="loadRates"
            >
              表示
            </button>
          </div>
        </div>
      </CCardBody>

      <template v-if="loaded">
        <CCardBody class="project-rate-result">
          <div class="project-rate-heading">
            <div>
              <p>MONTHLY PROJECT RATE</p>
              <h3>{{ loadedYear }}年{{ loadedMonth }}月 金額設定</h3>
            </div>
            <div class="project-rate-heading__actions">
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="saving || items.length === 0 || previousConfiguredCount === 0"
                @click="copyPreviousAll"
              >
                直近金額を一括反映
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="saving || editedCount === 0 || hasValidationError"
                @click="saveRates"
              >
                <span v-if="saving" class="my-spinner" aria-hidden="true"></span>
                {{ saving ? '保存中' : `変更した${editedCount}件を保存` }}
              </button>
            </div>
          </div>

          <div class="project-rate-guide">
            <div>
              <strong>基本請求額</strong>
              <span>請求書の基本請求額とプロジェクト集計の見込売上計算に使用します。</span>
            </div>
            <div>
              <strong>月額原価</strong>
              <span>プロジェクト集計の原価・粗利計算に使用します。</span>
            </div>
            <div>
              <strong>継続と当月除外</strong>
              <span>最新の設定金額は翌月以降も自動継続します。不要な月だけ「当月除外」を保存できます。</span>
            </div>
          </div>

          <div v-if="items.length" class="project-rate-bulk">
            <div class="project-rate-bulk__description">
              <strong>月額原価を原価率で一括計算</strong>
              <span>
                基本請求額 × 原価率を、当月対象の{{ bulkCostTargetCount }}件へ反映します。
                既に入力済みの月額原価は上書きされます。
              </span>
              <small v-if="bulkCostActiveCount !== bulkCostTargetCount">
                基本請求額が未入力・不正な{{ bulkCostActiveCount - bulkCostTargetCount }}件は一括計算の対象外です。
              </small>
            </div>

            <div class="project-rate-bulk__form">
              <label for="project-rate-cost-percentage">原価率</label>
              <div class="project-rate-bulk__controls">
                <div class="project-rate-percentage-input">
                  <input
                    id="project-rate-cost-percentage"
                    v-model="costRatePercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    inputmode="decimal"
                    class="form-control text-right"
                    :class="{ 'is-invalid': costRatePercent !== '' && !hasValidCostRatePercent }"
                    :disabled="saving"
                    :aria-invalid="costRatePercent !== '' && !hasValidCostRatePercent ? 'true' : 'false'"
                    aria-describedby="project-rate-cost-percentage-help"
                    @input="clearBulkCostResult"
                    @keydown.enter.prevent="applyCostRateToAll"
                  >
                  <span>%</span>
                </div>
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :disabled="!canApplyCostRate"
                  @click="applyCostRateToAll"
                >
                  月額原価へ一括反映
                </button>
              </div>
              <small
                id="project-rate-cost-percentage-help"
                :class="{ error: costRatePercent !== '' && !hasValidCostRatePercent }"
              >
                0〜100の数値を小数第2位まで入力できます。1円未満は四捨五入します。
              </small>
            </div>

            <p v-if="bulkCostResult" class="project-rate-bulk__result" role="status">
              <CIcon name="cil-check-circle" aria-hidden="true"/>
              {{ formatPercentage(bulkCostResult.percentage) }}%を{{ bulkCostResult.appliedCount }}件へ反映しました。
              <span v-if="bulkCostResult.skippedCount">
                {{ bulkCostResult.skippedCount }}件は基本請求額が未入力または不正なため対象外です。
              </span>
              保存前に各行の月額原価を確認してください。
            </p>
          </div>

          <div class="alert alert-info">
            管理者向けの全体設定です。ログイン中アカウントの全プロジェクト・全メンバーを表示します。
          </div>

          <div v-if="items.length === 0" class="preika-empty-state">
            <div>
              <strong>金額設定対象のメンバーがありません</strong>
              <p>プロジェクト所属または対象月の勤務実績を確認してください。</p>
            </div>
          </div>

          <!-- lang="ja"：取引先名・プロジェクト名の長い名前を、文節で折る（「ベータソリューションズ株式／会社」と最後の字が落ちないように）。 -->
          <div v-else class="table-responsive project-rate-table" lang="ja">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th>取引先</th>
                  <th>プロジェクト</th>
                  <th>メンバー</th>
                  <th class="text-center">精算基準時間</th>
                  <th class="text-right">直近請求額</th>
                  <th class="text-right">直近原価</th>
                  <th class="text-right">基本請求額</th>
                  <th class="text-right">月額原価</th>
                  <th class="text-center">当月対象</th>
                  <th class="text-center">状態</th>
                  <th class="text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in groupedItems">
                  <tr
                    v-for="(item, index) in group.items"
                    :key="item.projectId + '-' + item.userId"
                  >
                    <td
                      v-if="index === 0"
                      :rowspan="group.items.length"
                      class="project-rate-table__group-cell"
                    >
                      {{ group.companyName }}
                    </td>
                    <td
                      v-if="index === 0"
                      :rowspan="group.items.length"
                      class="project-rate-table__group-cell"
                    >
                      <strong>{{ group.projectName }}</strong>
                    </td>
                    <td class="project-rate-table__member">{{ item.userName }}</td>
                    <td class="text-center">
                      {{ item.baseTimeMin || '-' }}〜{{ item.baseTimeMax || '-' }}h
                    </td>
                    <td class="text-right project-rate-table__previous">
                      {{ formatCurrency(item.previousBillingUnitPrice) }}
                      <small v-if="item.inheritedYearMonth">
                        {{ formatYearMonth(item.inheritedYearMonth) }}設定
                      </small>
                    </td>
                    <td class="text-right project-rate-table__previous">
                      {{ formatCurrency(item.previousCostUnitPrice) }}
                    </td>
                    <td class="project-rate-table__input">
                      <CInput
                        v-model="item.billingUnitPrice"
                        type="number"
                        min="0"
                        step="1"
                        inputmode="numeric"
                        class="text-right"
                        :disabled="saving || item.excluded"
                        :is-valid="showPriceError(item, item.billingUnitPrice) ? false : null"
                        @input="markEdited(item)"
                      />
                      <small v-if="showPriceError(item, item.billingUnitPrice)" class="error">
                        0以上の整数で入力してください。
                      </small>
                    </td>
                    <td class="project-rate-table__input">
                      <CInput
                        v-model="item.costUnitPrice"
                        type="number"
                        min="0"
                        step="1"
                        inputmode="numeric"
                        class="text-right"
                        :disabled="saving || item.excluded"
                        :is-valid="showPriceError(item, item.costUnitPrice) ? false : null"
                        @input="markEdited(item)"
                      />
                      <small v-if="showPriceError(item, item.costUnitPrice)" class="error">
                        0以上の整数で入力してください。
                      </small>
                    </td>
                    <td class="text-center project-rate-table__exclusion">
                      <label class="project-rate-exclusion">
                        <input
                          v-model="item.excluded"
                          type="checkbox"
                          :disabled="saving || (!item.excluded && !item.excludable)"
                          @change="onExcludedChange(item)"
                        >
                        <span>当月除外</span>
                      </label>
                      <small v-if="!item.excludable">
                        勤務・売上実績あり
                      </small>
                    </td>
                    <td class="text-center">
                      <span
                        class="project-rate-status"
                        :class="rateStatusClass(item)"
                      >
                        {{ rateStatusLabel(item) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        :disabled="saving || item.excluded || !hasPreviousRate(item)"
                        @click="copyPrevious(item)"
                      >
                        直近反映
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="items.length" class="project-rate-footer">
            <p>
              最新の設定金額は翌月以降も自動継続します。当月だけ対象外にする場合は「当月除外」を保存してください。勤務・売上実績がある行は除外できません。
            </p>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="saving || editedCount === 0 || hasValidationError"
              @click="saveRates"
            >
              <span v-if="saving" class="my-spinner" aria-hidden="true"></span>
              {{ saving ? '保存中' : `変更した${editedCount}件を保存` }}
            </button>
          </div>
        </CCardBody>
      </template>
    </CCard>

    <div v-if="loading" class="loading-overlay" aria-live="polite" aria-busy="true">
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import ConstList from './../../ConstList'
import { requestFailureMessage } from '@/utils/requestFailure'
import {
  calculateProjectRateCost,
  isValidProjectRatePercentage
} from './../../utils/projectRateCalculation'

export default {
  name: 'ProjectRateSetting',

  data () {
    return {
      year: '',
      month: '',
      /*
       * 行を読み込んだ月。見出しと保存先はこちらを使う。
       * 選択欄は［表示］を押すまで行と食い違うことがあり、選択欄の月へ保存すると、
       * 別の月の画面の値でその月の金額設定と売上の原価を上書きする（2026-09-30）。
       */
      loadedYear: '',
      loadedMonth: '',
      yearOption: [],
      monthOption: [],
      loaded: false,
      loading: false,
      saving: false,
      items: [],
      previousYearMonth: '',
      costRatePercent: '',
      bulkCostResult: null
    }
  },

  computed: {
    groupedItems () {
      const groups = []
      const indexByProject = {}

      this.items.forEach(item => {
        const key = String(item.projectId)
        let groupIndex = indexByProject[key]

        if (groupIndex === undefined) {
          groupIndex = groups.length
          indexByProject[key] = groupIndex
          groups.push({
            projectId: item.projectId,
            projectName: item.projectName,
            companyName: item.companyName,
            items: []
          })
        }
        groups[groupIndex].items.push(item)
      })

      return groups
    },

    /*
     * 見るのは、保存で送る行（直した行）だけ。
     * 金額設定の無い人は空欄で出すので、触っていない空欄まで誤りに数えると、
     * 1人も直せないまま保存が止まる。
     */
    hasValidationError () {
      return this.items.some(item =>
        item.edited && !item.excluded && (
          !this.isValidPrice(item.billingUnitPrice) ||
          !this.isValidPrice(item.costUnitPrice)
        )
      )
    },

    /** 保存で送る行の数。ボタンの「変更したn件を保存」もこの数。 */
    editedCount () {
      return this.items.filter(item => item.edited).length
    },

    previousConfiguredCount () {
      return this.items.filter(item => !item.excluded && this.hasPreviousRate(item)).length
    },

    hasValidCostRatePercent () {
      return isValidProjectRatePercentage(this.costRatePercent)
    },

    bulkCostActiveCount () {
      return this.items.filter(item => !item.excluded).length
    },

    bulkCostTargetCount () {
      return this.items.filter(item =>
        !item.excluded && this.isValidPrice(item.billingUnitPrice)
      ).length
    },

    canApplyCostRate () {
      return !this.saving &&
        this.hasValidCostRatePercent &&
        this.bulkCostTargetCount > 0
    }
  },

  created () {
    this.yearOption = ConstList.YEAR_OPTION
    this.monthOption = ConstList.MONTH_OPTION
  },

  mounted () {
    /*
     * 請求書管理の「金額設定が未登録」の断りから来たときは、その月で開く
     * （リンクが年月を付けて来る）。年月として読めるときだけ使う。
     */
    const asked = (this.$route && this.$route.query) || {}
    if (/^\d{4}$/.test(String(asked.year || '')) && /^([1-9]|1[0-2])$/.test(String(asked.month || ''))) {
      this.year = String(asked.year)
      this.month = String(asked.month)
      this.loadRates()
      return
    }

    const saved = sessionStorage.getItem('project-rate-search-params')
    if (saved) {
      const params = JSON.parse(saved)
      this.year = params.year
      this.month = params.month
      this.loadRates()
      return
    }

    const target = moment()
    this.year = target.format('YYYY')
    this.month = target.format('M')
    this.loadRates()
  },

  methods: {
    loadRates () {
      if (this.loading || this.saving) return

      this.loading = true
      this.loaded = false
      this.costRatePercent = ''
      this.bulkCostResult = null
      const year = this.year
      const month = this.month

      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/admin/project-rate', {
          params: {
            year,
            month
          }
        })
        .then(response => {
          const data = response.data || {}
          this.previousYearMonth = data.previousYearMonth || ''
          this.items = (Array.isArray(data.items) ? data.items : []).map(row => ({
            ...row,
            /*
             * 金額設定が1件も無い人は、サーバが null で返す。0 に置き換えない。
             * 0 と見せると、保存で「設定済・0円」として書いてしまう（2026-09-30）。
             */
            billingUnitPrice: this.priceOrBlank(row.billingUnitPrice),
            costUnitPrice: this.priceOrBlank(row.costUnitPrice),
            /* 前の設定が無い人も null で返る。直近の欄は「—」と出し、明示の0円と分ける。 */
            previousBillingUnitPrice: this.priceOrBlank(row.previousBillingUnitPrice),
            previousCostUnitPrice: this.priceOrBlank(row.previousCostUnitPrice),
            inheritedYearMonth: row.inheritedYearMonth || '',
            inherited: row.inherited === true,
            excluded: row.excluded === true,
            excludable: row.excludable !== false,
            edited: false
          }))
          this.loadedYear = year
          this.loadedMonth = month
          this.loaded = true

          sessionStorage.setItem(
            'project-rate-search-params',
            JSON.stringify({ year, month })
          )
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * 直した行だけを送る。
     *
     * 一覧の全行を送っていたころは、1人だけ直して押すと、触っていない人まで
     * その月の金額として書いていた。金額設定の無い人は 0円の「設定済」になり、
     * 同じ月の売上の原価まで 0円で上書きされた。継続している人の金額も
     * その月の値として固定され、あとで前の月を直しても伝わらなくなった
     * （2026-09-30）。ヘルプの「変更行数を確認して確定します」のとおりにする。
     */
    saveRates () {
      if (this.saving || this.editedCount === 0 || this.hasValidationError) return

      this.saving = true
      /*
       * 送るのは見出しに出している月（読み込んだ月）。選択欄も戻し、
       * 保存したあとの読み直しが同じ月を読むようにする。
       */
      this.year = this.loadedYear
      this.month = this.loadedMonth
      const payload = {
        year: this.loadedYear,
        month: this.loadedMonth,
        items: this.items.filter(item => item.edited).map(item => ({
          project_id: item.projectId,
          user_id: item.userId,
          billing_unit_price: this.priceForSave(item.billingUnitPrice),
          cost_unit_price: this.priceForSave(item.costUnitPrice),
          excluded: item.excluded === true
        }))
      }

      axios
        .post(
          process.env.VUE_APP_API_ORIGIN + '/admin/project-rate',
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then(() => {
          alert(ConstList.REGISTER_MESSAGE)
          this.saving = false
          this.loadRates()
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.saving = false
        })
    },

    applyCostRateToAll () {
      if (!this.canApplyCostRate) return

      const percentage = Number(this.costRatePercent)
      const targets = this.items
        .filter(item => !item.excluded && this.isValidPrice(item.billingUnitPrice))
        .map(item => ({
          item,
          costUnitPrice: calculateProjectRateCost(item.billingUnitPrice, percentage)
        }))
        .filter(target => target.costUnitPrice !== null)

      if (!targets.length) return

      const confirmed = window.confirm(
        `基本請求額の${this.formatPercentage(percentage)}%を月額原価として、${targets.length}件へ一括反映します。\n` +
        '既に入力済みの月額原価も上書きされます。よろしいですか？'
      )
      if (!confirmed) return

      targets.forEach(target => {
        target.item.costUnitPrice = target.costUnitPrice
        target.item.edited = true
      })

      this.bulkCostResult = {
        percentage,
        appliedCount: targets.length,
        skippedCount: this.bulkCostActiveCount - targets.length
      }
    },

    copyPrevious (item) {
      if (item.excluded || !this.hasPreviousRate(item)) return
      item.billingUnitPrice = this.normalizePrice(item.previousBillingUnitPrice)
      item.costUnitPrice = this.normalizePrice(item.previousCostUnitPrice)
      this.markEdited(item)
    },

    copyPreviousAll () {
      this.items.forEach(item => {
        if (!item.excluded && this.hasPreviousRate(item)) {
          this.copyPrevious(item)
        }
      })
    },

    hasPreviousRate (item) {
      return Boolean(item.inheritedYearMonth) ||
        Number(item.previousBillingUnitPrice) > 0 ||
        Number(item.previousCostUnitPrice) > 0
    },

    /*
     * 除外を外したとき、金額が 0/0 なら直近の金額へ戻す。直近が無ければ空欄にして入力を求める。
     * 金額設定の無い人を除外で保存すると、サーバは 0/0 の除外行として持つ。その 0 を
     * 入力値として残すと、0円の「設定済」として保存できてしまう（2026-09-30）。
     */
    onExcludedChange (item) {
      if (!item.excluded &&
          Number(item.billingUnitPrice) === 0 && Number(item.costUnitPrice) === 0) {
        if (this.hasPreviousRate(item)) {
          this.copyPrevious(item)
        } else {
          item.billingUnitPrice = ''
          item.costUnitPrice = ''
        }
      }
      this.markEdited(item)
    },

    rateStatusLabel (item) {
      if (item.excluded) return '当月除外'
      if (item.configured) return '設定済'
      if (item.inherited) return '継続'
      return '未設定'
    },

    rateStatusClass (item) {
      if (item.excluded) return 'project-rate-status--excluded'
      if (item.configured) return 'project-rate-status--saved'
      if (item.inherited) return 'project-rate-status--inherited'
      return 'project-rate-status--new'
    },

    formatYearMonth (value) {
      const text = String(value || '')
      if (!/^\d{6}$/.test(text)) return ''
      return `${text.slice(0, 4)}年${Number(text.slice(4, 6))}月`
    },

    markEdited (item) {
      item.edited = true
      this.clearBulkCostResult()
    },

    clearBulkCostResult () {
      this.bulkCostResult = null
    },

    isValidPrice (value) {
      if (value === '' || value === null || value === undefined) return false
      const number = Number(value)
      return Number.isFinite(number) && number >= 0 && Number.isInteger(number) && number <= 999999999999
    },

    /** 入力の誤りを出すのは、直した行（保存で送る行）だけ。 */
    showPriceError (item, value) {
      return item.edited === true && !item.excluded && !this.isValidPrice(value)
    },

    /** 未設定（null）は空欄のまま見せる。0 にしない。 */
    priceOrBlank (value) {
      if (value === '' || value === null || value === undefined) return ''
      return this.normalizePrice(value)
    },

    /**
     * 送る値。空欄は null で送り、0 にしない。
     * 当月除外の行はサーバが 0 として保存し、それ以外の空欄は
     * hasValidationError が送らせない（サーバも「入力してください」で断る）。
     */
    priceForSave (value) {
      if (value === '' || value === null || value === undefined) return null
      return Number(value)
    },

    normalizePrice (value) {
      const number = Number(value)
      return Number.isFinite(number) && number >= 0 ? Math.round(number) : 0
    },

    /** 直近の欄。前の設定が無い（空欄）ときは「—」。0円は「0」。 */
    formatCurrency (value) {
      if (value === '' || value === null || value === undefined) return '—'
      return this.normalizePrice(value).toLocaleString('ja-JP')
    },

    formatPercentage (value) {
      const percentage = Number(value)
      if (!Number.isFinite(percentage)) return ''
      return percentage.toLocaleString('ja-JP', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },

    handleError (error) {
      axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
          { error: String(error) },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then(() => alert(requestFailureMessage(error)))
        .catch(() => alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR)))
    }
  }
}
</script>

<style scoped>
.project-rate-search,
.project-rate-heading,
.project-rate-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.project-rate-search__period,
.project-rate-search__actions,
.project-rate-heading__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-rate-search__period .custom-select {
  width: 100px;
}

/* 月の欄。78px では「10月」の「月」が右の矢印に重なった。 */
.project-rate-search__period .custom-select:nth-child(2) {
  width: 90px;
}

/* 上の［直近金額を一括反映］［変更したn件を保存］は折らない（狭いと「保／存」と割れた）。 */
.project-rate-heading__actions .btn {
  white-space: nowrap;
}

.project-rate-result {
  border-top: 1px solid var(--preika-border);
}

.project-rate-heading {
  margin-bottom: 18px;
}

.project-rate-heading p {
  margin: 0 0 4px;
  color: var(--preika-blue-600);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .15em;
}

.project-rate-heading h3 {
  margin: 0;
  color: var(--preika-navy-900);
  font-size: 22px;
}

.project-rate-guide {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 18px;
}

.project-rate-guide div {
  padding: 14px 16px;
  border: 1px solid var(--preika-blue-200);
  border-radius: 12px;
  background: var(--preika-blue-050);
}

.project-rate-guide strong,
.project-rate-guide span {
  display: block;
}

.project-rate-guide strong {
  margin-bottom: 4px;
  color: var(--preika-blue-900);
  font-size: 12px;
}

.project-rate-guide span {
  color: var(--preika-text-muted);
  font-size: 12px;
  line-height: 1.7;
}

.project-rate-bulk {
  display: grid;
  align-items: center;
  margin-bottom: 18px;
  padding: 16px;
  border: 1px solid var(--preika-blue-200);
  border-radius: 12px;
  background: linear-gradient(135deg, #f7fbfe 0%, #eef7fc 100%);
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px 22px;
}

.project-rate-bulk__description strong,
.project-rate-bulk__description span,
.project-rate-bulk__description small {
  display: block;
}

.project-rate-bulk__description strong {
  margin-bottom: 5px;
  color: var(--preika-blue-900);
  font-size: 12px;
}

.project-rate-bulk__description span {
  color: var(--preika-text-muted);
  font-size: 12px;
  line-height: 1.7;
}

.project-rate-bulk__description small {
  margin-top: 5px;
  color: var(--preika-warning-700);
  font-size: 12px;
}

.project-rate-bulk__form {
  min-width: 390px;
}

.project-rate-bulk__form > label {
  display: block;
  margin-bottom: 5px;
  color: var(--preika-navy-800);
  font-size: 12px;
  font-weight: 800;
}

.project-rate-bulk__controls {
  display: flex;
  align-items: center;
  gap: 9px;
}

.project-rate-percentage-input {
  display: flex;
  width: 135px;
  align-items: center;
  overflow: hidden;
  border: 1px solid var(--preika-border);
  border-radius: 8px;
  background: #fff;
}

.project-rate-percentage-input:focus-within {
  border-color: var(--preika-blue-500);
  box-shadow: 0 0 0 3px rgba(32, 132, 196, .12);
}

.project-rate-percentage-input .form-control {
  height: 36px;
  border: 0;
  box-shadow: none;
  font-size: 12px;
}

.project-rate-percentage-input .form-control.is-invalid {
  background-image: none;
}

.project-rate-percentage-input > span {
  padding-right: 10px;
  color: var(--preika-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.project-rate-bulk__form > small {
  display: block;
  margin-top: 5px;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-rate-bulk__form > small.error {
  color: var(--danger);
}

.project-rate-bulk__result {
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 9px 11px;
  border-radius: 8px;
  background: var(--preika-success-100);
  color: var(--preika-success-700);
  font-size: 12px;
  line-height: 1.6;
  grid-column: 1 / -1;
  gap: 6px;
}

.project-rate-bulk__result .c-icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.project-rate-table__group-cell {
  min-width: 150px;
  background: var(--preika-gray-050);
  vertical-align: middle;
}

.project-rate-table__previous {
  color: var(--preika-text-muted);
  background: #fbfcfd;
}

.project-rate-table__previous small {
  display: block;
  margin-top: 3px;
  font-size: 12px;
  white-space: nowrap;
}

/*
  メンバーの名前・状態の札・「当月除外」・行のボタンは折らない（字を 12px にしたら、表の幅に押されて
  「ローカル／経営管／理者」「当／月／除／外」と1字ずつ割れた）。表は横に送れる（table-responsive）。
*/
.project-rate-table__member,
.project-rate-table td .btn {
  white-space: nowrap;
}

/* 取引先名・プロジェクト名など、折れる欄は文節で折る（Chrome・Edge で効く）。 */
.project-rate-table td {
  word-break: auto-phrase;
}

/* 表の下の［変更したn件を保存］も折らない（上のボタンと同じ）。 */
.project-rate-footer .btn {
  white-space: nowrap;
}

.project-rate-table__input {
  min-width: 150px;
}

.project-rate-table__input .form-group {
  margin-bottom: 0;
}

.project-rate-table__input .error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
}

.project-rate-status {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.project-rate-status--saved {
  background: var(--preika-success-100);
  color: var(--preika-success-700);
}

.project-rate-status--new {
  background: var(--preika-warning-100);
  color: var(--preika-warning-700);
}

.project-rate-status--inherited {
  background: var(--preika-blue-100);
  color: var(--preika-blue-800);
}

.project-rate-status--excluded {
  background: var(--preika-gray-150);
  color: var(--preika-gray-700);
}

.project-rate-table__exclusion {
  min-width: 130px;
}

.project-rate-exclusion {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.project-rate-exclusion input {
  width: 16px;
  height: 16px;
}

.project-rate-table__exclusion small {
  display: block;
  margin-top: 4px;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-rate-footer {
  align-items: center;
  margin-top: 18px;
  padding: 16px;
  border: 1px solid var(--preika-border);
  border-radius: 12px;
  background: var(--preika-gray-050);
}

.project-rate-footer p {
  margin: 0;
  color: var(--preika-text-muted);
  font-size: 12px;
}

@media (max-width: 767.98px) {
  .project-rate-search,
  .project-rate-heading,
  .project-rate-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .project-rate-search__actions .btn,
  .project-rate-heading__actions .btn {
    flex: 1;
  }

  .project-rate-guide {
    grid-template-columns: 1fr;
  }

  .project-rate-bulk {
    grid-template-columns: 1fr;
  }

  .project-rate-bulk__form {
    min-width: 0;
  }

  .project-rate-bulk__controls {
    align-items: stretch;
    flex-direction: column;
  }

  .project-rate-percentage-input {
    width: 100%;
  }
}
</style>
